
import { Button } from "@/components/ui/button";
import { useTranslatedText } from "@/hooks/useTranslate";
import { useNavigate } from "react-router-dom";
import { Code, Users, Award, Lightbulb } from "lucide-react";

const AboutSection = () => {
  const navigate = useNavigate();

  const titleText = useTranslatedText("Quiénes somos");
  const subtitleText = useTranslatedText("Únete a nosotros para un viaje hacia el éxito respaldado por años de excelencia comprobada.");
  const saberMasText = useTranslatedText("Saber más");

  const feature1Title = useTranslatedText("Más de 5 años liderando");
  const feature1Desc = useTranslatedText("la convergencia tecnológica y estratégica.");
  const feature2Title = useTranslatedText("Nuestros proyectos están");
  const feature2Desc = useTranslatedText("metodológicamente diseñados para el éxito.");
  const feature3Title = useTranslatedText("Ofrecemos soluciones");
  const feature3Desc = useTranslatedText("que rompen paradigmas tradicionales.");

  const innovacionTitle = useTranslatedText("Innovación");
  const innovacionDesc = useTranslatedText("Utilizamos las últimas tecnologías");
  const equipoTitle = useTranslatedText("Equipo Experto");
  const equipoDesc = useTranslatedText("Profesionales especializados");
  const calidadTitle = useTranslatedText("Calidad Garantizada");
  const calidadDesc = useTranslatedText("Resultados excepcionales");
  const solucionesTitle = useTranslatedText("Soluciones Personalizadas");
  const solucionesDesc = useTranslatedText("Adaptadas a tus necesidades");

  const features = [
    { number: "01", title: feature1Title, description: feature1Desc },
    { number: "02", title: feature2Title, description: feature2Desc },
    { number: "03", title: feature3Title, description: feature3Desc },
  ];

  const infoCards = [
    { icon: <Lightbulb className="w-10 h-10" />, title: innovacionTitle, description: innovacionDesc },
    { icon: <Users className="w-10 h-10" />, title: equipoTitle, description: equipoDesc },
    { icon: <Award className="w-10 h-10" />, title: calidadTitle, description: calidadDesc },
    { icon: <Code className="w-10 h-10" />, title: solucionesTitle, description: solucionesDesc },
  ];

  return (
    <section id="nosotros" className="py-20 bg-techno-dark relative">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-techno-purple/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-5xl font-bold text-white">
                <span className="text-techno-purple">¿</span>{titleText}
                <span className="text-techno-purple">?</span>
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed">{subtitleText}</p>
            </div>

            <Button
              className="bg-button-gradient hover:opacity-90 transition-opacity duration-300"
              onClick={() => navigate('/nosotros')}
            >
              {saberMasText}
            </Button>

            <div className="space-y-6 mt-12">
              {features.map((feature, index) => (
                <div key={index} className="animate-fade-in" style={{ animationDelay: `${index * 0.2}s` }}>
                  <div className="flex items-start space-x-4">
                    <div className="text-3xl font-bold text-techno-purple">{feature.number}</div>
                    <div>
                      <h3 className="text-white font-semibold text-lg">{feature.title}</h3>
                      <p className="text-gray-400">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Glassmorphism Cards */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-6">
              {infoCards.map((card, index) => (
                <div
                  key={index}
                  className="glass-strong rounded-2xl p-6 hover:border-techno-purple/40 transition-all duration-500 group animate-fade-in hover:scale-105 hover:-translate-y-1"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="space-y-4">
                    <div className="text-techno-purple group-hover:scale-110 transition-transform duration-300">
                      {card.icon}
                    </div>
                    <h3 className="text-white font-semibold text-lg">{card.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{card.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Section divider gradient line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-techno-purple/40 to-transparent"></div>
    </section>
  );
};

export default AboutSection;
